import * as React from "react";
import { Quote } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
const narratives = [{
  id: "mentorship",
  theme: "Zásady Ochrany Dat",
  headline: "Ochrana Dat",
  quote: "Upřednostňujeme bezpečnost vašich osobních údajů nade vše. Naše protokoly zajišťují, že vaše data zůstávají důvěrná a jsou používána výhradně k vylepšení vašeho zážitku s našimi službami.",
  author: "Právní Oddělení",
  role: "Tým Compliance",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/legal_privacy_shield.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/legal_privacy_shield.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/legal_privacy_shield.png-wvc-sizes",
    alt: "Abstract representation of data security and privacy"
  },
  avatar: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/avatar_legal_team.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/avatar_legal_team.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/avatar_legal_team.png-wvc-sizes",
    alt: "Legal team icon"
  }
}, {
  id: "healing",
  theme: "Podmínky Služeb",
  headline: "Pokyny pro Používání",
  quote: "Přístupem k naší platformě souhlasíte s dodržováním standardu profesionálního chování. Tyto pokyny jsou navrženy tak, aby podporovaly bezpečné a respektující prostředí pro všechny naše klienty i zaměstnance.",
  author: "Provoz",
  role: "Vedení",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/legal_service_agreement.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/legal_service_agreement.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/legal_service_agreement.png-wvc-sizes",
    alt: "Professional service agreement document"
  },
  avatar: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/avatar_ops_manager.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/avatar_ops_manager.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/avatar_ops_manager.png-wvc-sizes",
    alt: "Operations manager icon"
  }
}, {
  id: "reentry",
  theme: "Právní Odpovědnost",
  headline: "Právní Hranice",
  quote: "Pochopení rozsahu našich povinností je klíčové pro transparentní vztah. Jasně definujeme hranice naší odpovědnosti, aby byly obě strany chráněny zákonem.",
  author: "Právní Poradce",
  role: "Právní Asistent",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/legal_liability_overview.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/legal_liability_overview.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/legal_liability_overview.png-wvc-sizes",
    alt: "Legal scales representing balance and liability"
  },
  avatar: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/avatar_counsel.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/avatar_counsel.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/avatar_counsel.png-wvc-sizes",
    alt: "Legal advisor icon"
  }
}];
export default function WhySupportMatters() {
  return <section data-section-id="243355" id="legal-overview" className="bg-background text-foreground py-20 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <h2 className="font-default text-4xl md:text-7xl font-bold text-foreground mb-6 tracking-tighter">
            Podmínky a Ujednání
          </h2>
          <p className="font-default text-lg md:text-xl text-muted-foreground leading-relaxed">
            Vítejte v našem právním přehledu. Tato sekce popisuje rámec našeho profesionálního vztahu, 
            zajišťuje transparentnost, bezpečnost a vzájemný respekt v každé interakci s našimi službami.
          </p>
          {}
          <div className="mt-12 h-px w-full bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {narratives.map((story, i) => <Card key={story.id} data-index={i} className="flex flex-col h-full overflow-hidden border-border shadow-sm hover:shadow-lg transition-all duration-500 bg-card/50 backdrop-blur-sm group rounded-2xl">
              <div className="relative h-64 w-full overflow-hidden">
                <img src={story.image.src} data-wvc-srcset={story.image.srcset} data-wvc-sizes={story.image.sizes} alt={story.image.alt} className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-110" loading="lazy" />
                <div className="absolute top-4 left-4">
                  <Badge variant="secondary" className="bg-background/80 backdrop-blur-md text-primary font-default font-semibold shadow-sm border-primary/10">
                    {story.theme}
                  </Badge>
                </div>
              </div>

              <CardHeader className="pb-4 pt-8 px-6 md:px-8">
                <CardTitle className="font-default text-2xl text-foreground tracking-tight">
                  {story.headline}
                </CardTitle>
              </CardHeader>

              <CardContent className="flex-grow px-6 md:px-8">
                <div className="relative">
                  <Quote className="absolute -top-3 -left-2 size-10 text-primary/5 rotate-180" aria-hidden="true" />
                  <p className="font-default text-muted-foreground leading-relaxed relative z-10 pl-5 border-l-2 border-primary/30 italic">
                    "{story.quote}"
                  </p>
                </div>
              </CardContent>

              <CardFooter className="pt-6 pb-8 px-6 md:px-8 border-t border-border mt-auto bg-accent/30">
                <div className="flex items-center gap-4">
                  
                  <div className="flex flex-col">
                    
                    
                  </div>
                </div>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}